import React from 'react';
import { history } from 'umi';
import moment from 'moment';
import tinymce from 'tinymce/tinymce';
import { updateURLSearchParams } from '@intelligence/utils/commonUtils';
import { isEmpty, isMockId, transform2MockId, reportBtnOperationLog } from '@gp/utils/common';
// @ts-ignore
import { storage } from '@gp/utils/utils';
import { DPS_REPORT_STORAGE_KEY } from '@gp/constants/storageKeys';

import type { IMockIdable } from '@gp/types/global';
import type {
  IDPSReportBaseVO,
  IDPSReportCoreDTO,
  IDPSReportContextDTO,
  IDPSReportHelperVO,
  IDPSReportProjectSimpleDTO,
} from '@gp/types/dpsReport/common';

export const getInitialReportId = (reports?: IDPSReportBaseVO[] | null): string => {
  const defaultId = reports?.[0]?.id || '';
  const { from, start_date: startDate, end_date: endDate } = history.location.query as any;

  // 从dps周报邮件 点击图片 跳转dps周报预览页
  if (!!startDate && !!endDate) {
    const report = reports?.find((report) => {
      const { start_date: reportStartDate, end_date: reportEndDate } = report || {};
      return reportStartDate === startDate && reportEndDate === endDate;
    });
    if (from === 'email') {
      updateURLSearchParams({}, ['from']);
      // 打点记录
      reportBtnOperationLog('WORKSPACE_DPS_WEEKLY_REPORT_ACCESS_FROM_EMAIL_SUBMIT');
    }
    return report?.id || defaultId || '';
  }

  // 本页面新增、编辑后 返回dps周报预览页
  const sessionReportId = storage.getSessionStorageItem(DPS_REPORT_STORAGE_KEY.DPS_REPORT_REPORT_ID, true) || '';
  const sessionId = reports?.find((report) => report?.report_id === sessionReportId)?.id || '';
  return sessionId || defaultId || '';
};

/**
 * 组装DPS周报页面副标题中日期字符串的子标题，字符串格式为Week 19 (2024 May 6-May 10)
 * @param dpsReport DPS周报（VO）
 * @return DPS周报页面副标题中的日期子标题
 */
export const getDPSReportDateTitle = (dpsReport?: IDPSReportHelperVO | null): string => {
  if (!dpsReport) return '';
  const {
    start_date_dps_report_week: startDateDPSReportWeek,
    end_date_dps_report_week: endDateDPSReportWeek,
    year,
    weeks_in_year: weeksInYear,
  } = dpsReport || {};
  const startDateRFC = moment(startDateDPSReportWeek);
  const startDate = startDateRFC.format('D');
  const startMonth = startDateRFC.format('MMM');
  const endDateRFC = moment(endDateDPSReportWeek);
  const endDate = endDateRFC.format('D');
  const endMonth = endDateRFC.format('MMM');
  return `Week ${weeksInYear} (${year} ${startMonth} ${startDate} - ${endMonth} ${endDate})`;
};

/**
 * 组装DPS周报 Select组件的Option的text
 * @param dpsReport DPS周报（VO）
 * @return Select组件的Option的text
 */
export const getDPSReportWeekLabel = (dpsReport: IDPSReportHelperVO) => {
  const {
    start_date_dps_report_week: startDateDPSReportWeek,
    end_date_dps_report_week: endDateDPSReportWeek,
    year,
    weeks_in_year: weeksInYear,
  } = dpsReport || {};
  return (
    <div>
      Week {weeksInYear}, {year}{' '}
      <span className="dbg-common-placeholder">
        ({startDateDPSReportWeek}~{endDateDPSReportWeek})
      </span>
    </div>
  );
};

/**
 * 过滤掉id、start_date、end_date为空的无效DPS周报
 * @param dpsReports DPS周报
 * @return DPS周报（id、start_date、end_date不为空）
 */
const filterDPSReports = (dpsReports?: IDPSReportCoreDTO[] | null): IDPSReportCoreDTO[] => {
  if (!Array.isArray(dpsReports) || !dpsReports.length) return [];
  return dpsReports.filter((dpsReport) => {
    const { type, id, report_id: reportId, start_date: startDate, end_date: endDate } = dpsReport || {};
    return !!type && !!id && !!reportId && !!startDate && !!endDate;
  });
};

/**
 * 获取一周的开始日期，优先使用开始日期，开始日期无效时，使用结束日期反推开始日期（结束日期减去6天作为开始日期）
 * @param startDate 开始日期
 * @param endDate 结束日期
 * @return 一周的开始日期
 */
const getStartDateMOfWeek = (startDate?: string, endDate?: string): moment.Moment | null => {
  const startDateM: moment.Moment | null = startDate ? moment(startDate) : null;
  if (startDateM?.isValid()) return startDateM?.startOf('week');
  const endDateM: moment.Moment | null = endDate ? moment(endDate) : null;
  if (!endDateM?.isValid()) return null;
  return endDateM?.subtract(6, 'days').startOf('week');
};

/**
 * 转为周时间为符合DPS周报要求的时间格式（每周的星期一到星期五）
 * @param startDate 开始日期
 * @param endDate 结束日期
 * @return 符合DPS周报要求的时间格式（每周的星期一到星期五）
 */
export const getWeekDateOfDPSReport = (
  startDate?: string,
  endDate?: string,
): {
  startDateM: moment.Moment | null;
  startDate: string;
  endDate: string;
} => {
  // 一周的开始日期
  const startDateM: moment.Moment | null = getStartDateMOfWeek(startDate, endDate);
  // 一周的开始日期（符合DPS周报要求 星期一作为一周的开始日期）
  const startDateMDPSReportWeek: moment.Moment | null = startDateM?.isValid()
    ? moment(startDateM).startOf('week')
    : null;
  // 一周的结束日期（符合DPS周报要求 星期五作为一周的结束日期）
  const endDateMDPSReportWeek: moment.Moment | null = startDateMDPSReportWeek?.isValid()
    ? moment(startDateMDPSReportWeek).add(4, 'days')
    : null;
  const startDateDPSReportWeek = startDateMDPSReportWeek?.format('YYYY-MM-DD') || '';
  const endDateDPSReportWeek = endDateMDPSReportWeek?.format('YYYY-MM-DD') || '';
  return {
    startDateM,
    startDate: startDateDPSReportWeek,
    endDate: endDateDPSReportWeek,
  };
};

/**
 * 转换DPS周报（DTO）为 DPS周报（VO）
 * @param dpsReports DPS周报（DTO）
 * @return DPS周报（VO）
 */
const formatDPSReports = (dpsReports?: IDPSReportCoreDTO[]): IDPSReportBaseVO[] => {
  if (!Array.isArray(dpsReports) || !dpsReports.length) return [];

  return dpsReports.map((dpsReport) => {
    const { start_date: startDate, end_date: endDate } = dpsReport || {};

    // 转换接口返回的开始日期、结束日期 为 符合DPS周报要求的日期（星期一～星期五）
    const {
      startDateM: startDateMDPSReportWeek,
      startDate: startDateDPSReportWeek,
      endDate: endDateDPSReportWeek,
    } = getWeekDateOfDPSReport(startDate, endDate);

    return {
      ...dpsReport,
      start_date_dps_report_week: startDateDPSReportWeek,
      end_date_dps_report_week: endDateDPSReportWeek,
      year: startDateMDPSReportWeek?.get('weekYear') || Number.NaN,
      weeks_in_year: startDateMDPSReportWeek?.weeks() || Number.NaN,
    };
  });
};

/**
 * 获取处理后的DPS周报数据
 * 1、过滤掉id、start_date、end_date为空的无效DPS周报
 * 2、转换为VO
 * 3、按start_date_dps_report_week升序排序
 * @param dpsReports 接口返回的DPS周报数据（DTO）
 * @returns 处理后的DPS周报数据（VO）
 */
export const getDPSReports = (dpsReports?: IDPSReportCoreDTO[] | null): IDPSReportBaseVO[] => {
  if (!Array.isArray(dpsReports) || !dpsReports.length) return [];
  const filteredReports = filterDPSReports(dpsReports);
  return formatDPSReports(filteredReports);
};

/**
 * 获取新的当前DPS周报id（删除当前周报后，需要展示新的周报）
 * 如果当前被删除的DPS周报 后面还有DPS周报，就返回后面一个DPS周报的id，否则就返回前面的DPS周报的id，如果删除后一个DPS周报都没有，就返回空字符串
 * @param id 删除的DPS周报id
 * @param reports DPS周报（VO）
 * @return 新的当前DPS周报的id
 */
export const getNewCurrentReportIdWhenDeleteCurrentReport = (id?: string, reports?: IDPSReportBaseVO[]): string => {
  if (!Array.isArray(reports) || !reports.length) return '';
  if (!id) return '';
  const idx = reports.findIndex((report) => report?.id === id);
  // 获取没匹配到要删除的DPS周报，就返回当前要删除的DPS周报的id，避免页面没有展示DPS周报
  if (idx === -1) return id;
  // 如果当前只有一个DPS周报，并且被删除后，直接返回空字符串
  if (reports.length === 1) return '';
  // 如果当前被删除的DPS周报是最后一个DPS周报，就返回前面的DPS周报的id
  if (idx === reports.length - 1) return reports[idx - 1]?.id || '';
  // 如果当前被删除的DPS周报不是最后一个DPS周报，就返回后面的DPS周报的id
  return reports[idx + 1]?.id || '';
};

export const getIsDisabled = <T extends IMockIdable & IDPSReportProjectSimpleDTO>(cvm: T) => {
  const { mock_id: mockId, project_id: projectId } = cvm || {};
  if (!isMockId(mockId)) return false;
  return !projectId;
};

export const getCommonRepeatCheckKey = (data?: IDPSReportContextDTO): string => {
  const { project_id: projectId, milestone_id: milestoneId } = data || {};
  return !projectId || !milestoneId ? '' : `${projectId}_${milestoneId}`;
};

/**
 * 清空表格中富文本组件的value，富文本展示的文本内容
 * @param rootNode 表格富文本组件的父节点（祖辈节点）
 * @param rowIndex 表格行索引
 */
export const resetEditorContent = (rootNode?: Element | null, rowIndex?: number) => {
  if (isEmpty(rootNode)) return;
  if (isEmpty(rowIndex)) return;
  const rowNo = (rowIndex as number) + 1;
  const editorEl = rootNode!.querySelector(
    `.tea-table .tea-table__body .tea-table__box tr:nth-of-type(${rowNo}) .dbg-common-editor > textarea`,
  );
  const editorId = editorEl?.id || '';
  const editor = tinymce.get(editorId);
  editor?.setContent('');
};

export const formatComment = (payload?: string): string => {
  if (typeof payload !== 'string') return '';
  // 把span内敛样式实现的下划线 替换为u标签实现下划线
  return payload.replaceAll(/<span style="text-decoration: underline;">(.*)<\/span>/gi, '<u>$1</u>');
};

export const getStorageReportTitle = () =>
  storage.getSessionStorageItem(DPS_REPORT_STORAGE_KEY.DPS_REPORT_REPORT_TITLE) || {};

export const getStorageReport = () =>
  storage.getSessionStorageItem(DPS_REPORT_STORAGE_KEY.DPS_REPORT_REPORT_DETAILS) || {};

export const removeStorageReport = () =>
  storage.removeSessionStorageItem(DPS_REPORT_STORAGE_KEY.DPS_REPORT_REPORT_DETAILS);

export const transform2MockIdBatch = (list: any[]): any[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((item) => {
    const { mock_id: mockId } = item || {};
    return { ...item, mock_id: transform2MockId(mockId) };
  });
};

/**
 * 获取指定周日期是否禁用，指定周日期的开始日期，指定周日期的结束日期
 * @param weekM 指定周日期的moment
 * @param disabledWeeks 禁用周日期（已有周报的周日期）
 * @returns 指定周日期是否禁用，指定周日期的开始日期，指定周日期的结束日期
 */
const getInitialWeekData = (
  weekM?: moment.Moment,
  disabledWeeks?: string[][] | null,
): { disabled: boolean; startDate: string; endDate: string } => {
  if (!weekM?.isValid()) return { disabled: false, startDate: '', endDate: '' };
  const weekStartDate = weekM.startOf('week').format('YYYY-MM-DD');
  const weekEndDate = weekM.endOf('week').format('YYYY-MM-DD');
  const isDisabled = !!disabledWeeks?.find((disabledWeek) => {
    const [disabledWeekStartDate, disabledWeekEndDate] = disabledWeek || [];
    return disabledWeekStartDate === weekStartDate && disabledWeekEndDate === weekEndDate;
  });

  return { disabled: isDisabled, startDate: weekStartDate, endDate: weekEndDate };
};

/**
 * 获取新增周报默认选中的周日期
 * @param disabledWeeks 禁用周日期（已有周报的周日期）
 * @returns 新增周报默认选中的周日期
 */
export const getInitialWeekDate = (
  disabledWeeks?: string[][] | null,
): {
  start_date: string;
  end_date: string;
} => {
  const thePastWeekM = moment().subtract(1, 'weeks');
  const {
    disabled: thePastWeekDisabled,
    startDate: thePastWeekStartDate,
    endDate: thePastWeekEndDate,
  } = getInitialWeekData(thePastWeekM, disabledWeeks);
  if (!thePastWeekDisabled) return { start_date: thePastWeekStartDate, end_date: thePastWeekEndDate };

  const {
    disabled: currentWeekDisabled,
    startDate: currentWeekStartDate,
    endDate: currentWeekEndDate,
  } = getInitialWeekData(moment(), disabledWeeks);
  if (!currentWeekDisabled) return { start_date: currentWeekStartDate, end_date: currentWeekEndDate };

  return { start_date: '', end_date: '' };
};
